import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AUTO_ENTRY, isAutoRow } from '../../services/modelMenu';
import { deepLeft } from '../../services/meterFormat';

// The chat's model picker (founder, 2026-10-30: a proper redesign).
//
// It was rung headings (QUICK / CAREFUL / BIG) with blurbs like "About six
// Quick turns per turn", "plenty left" on every row, and a full teal bar on
// the chosen row. Now, as the server's menu says it (GET /api/plans/usage ->
// `menu`, docs/plans/chat-2.md):
//   - Auto first, recommended: it picks the model for each task and uses
//     less of the plan;
//   - then "Choose a model": each model this box can run, with one plain line
//     of what it is best for and its relative cost as dots. A model the box
//     cannot run is not shown at all; a model the plan does not include
//     (Free) is shown with a Pro pill that opens the plans page;
//   - Think harder, on Pro and Max: every message goes to the deep route
//     while it is on, and it counts down the day's allowance.
// A menu of radio items (WAI-ARIA menu pattern): arrows move, Enter or Space
// chooses, Escape closes and gives focus back to the button. On a phone it
// is a sheet from the bottom of the screen (AntigravitySidebar.css).

const COST_WORD = { 1: 'low', 2: 'medium', 3: 'high' };

/** The name on the button: "Auto", or the model without its maker ("Sonnet 5.5"). */
export const shortName = (m) => {
  if (!m || isAutoRow(m)) return 'Auto';
  return m.shortLabel || String(m.label || m.id).replace(/^Claude\s+/i, '');
};

/**
 * Auto's two lines, from the server's hint ("Picks the best model for each
 * task and uses 20% less of your plan."), so the share it saves is said in
 * one place. The fixed words are for a hint in another shape.
 */
export const autoLines = (hint) => {
  const m = /^(.+?)\s+and\s+(uses\s.+?)\.?$/i.exec(String(hint || '').trim());
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  return m ? [cap(m[1]), cap(m[2])] : ['Picks the best model for each task', 'Uses 20% less of your plan'];
};

/** ●○○ / ●●○ / ●●● for the server's `cost` (1-3), said in words to a screen reader. */
export function CostDots({ cost }) {
  const n = Math.round(Number(cost));
  if (!(n >= 1 && n <= 3)) return null;
  return (
    <span className="mp-cost">
      <span className="mp-dots" aria-hidden="true">{'●'.repeat(n)}<span className="mp-dots-off">{'○'.repeat(3 - n)}</span></span>
      <span className="visually-hidden">Cost: {COST_WORD[n]}</span>
    </span>
  );
}

const Check = ({ on }) => (
  <span className="mp-check material-symbols-rounded" aria-hidden="true">{on ? 'check' : ''}</span>
);

export default function ModelPicker({
  menu,
  activeModel,
  onSelect,
  plan = null,
  meter = null,
  thinkHarder = false,
  onThinkHarder = null,
}) {
  const [open, setOpen] = useState(false);
  const [focusOn, setFocusOn] = useState('chosen');
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const rows = Array.isArray(menu) && menu.length ? menu : [AUTO_ENTRY];
  const auto = rows.find(isAutoRow) || AUTO_ENTRY;
  // A model this box cannot run is hidden, not greyed: there is nothing the
  // person can do about it, so it is only noise (founder, 2026-10-30).
  const models = rows.filter(m => !isAutoRow(m) && m.available !== false);
  const chosen = rows.find(m => m.id === activeModel && m.allowed !== false && m.available !== false) || auto;
  const onAuto = isAutoRow(chosen);

  // Think harder is Pro's and Max's (Free's daily cap is 0).
  const left = deepLeft(meter);
  const showDeep = Boolean(onThinkHarder && plan && plan !== 'free' && meter?.deep?.capPerDay !== 0);
  const deepOut = left === 0;
  const deepOn = Boolean(thinkHarder && !deepOut);

  const items = () => Array.from(menuRef.current?.querySelectorAll('[data-mp-item]') || []);
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  };
  const openAt = (where) => { setFocusOn(where); setOpen(true); };

  // Where the menu fits (founder, 2026-10-30): it drops DOWN from the button,
  // never taller than the room below it (or 420 px), and only opens upward
  // when there is clearly more room above (the chat box at the bottom of the
  // window). It lines up with the button's right edge when its left edge
  // would run past the window. On a phone it is a sheet (CSS), so no sizing.
  const [place, setPlace] = useState(null);
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || typeof window === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(max-width: 520px)').matches) { setPlace(null); return; }
    const r = triggerRef.current.getBoundingClientRect();
    const below = Math.floor(window.innerHeight - r.bottom - 16);
    const above = Math.floor(r.top - 16);
    const down = below >= 260 || below >= above;
    const room = Math.max(200, down ? below : above);
    const width = Math.min(300, window.innerWidth - 24);
    const alignRight = r.left + width > window.innerWidth - 12;
    setPlace({
      maxHeight: Math.min(420, room),
      width,
      ...(down ? { top: 'calc(100% + 8px)', bottom: 'auto' } : { bottom: 'calc(100% + 8px)', top: 'auto' }),
      ...(alignRight ? { left: 'auto', right: 0 } : {}),
    });
  }, [open]);

  // Focus goes into the menu when it opens: the chosen row, or the last one
  // when it was opened with the up arrow.
  useEffect(() => {
    if (!open) return;
    const list = items();
    const target = focusOn === 'last'
      ? list[list.length - 1]
      : menuRef.current?.querySelector('[role="menuitemradio"][aria-checked="true"]') || list[0];
    // preventScroll: this runs a beat before the menu takes its place above
    // the button, while it still hangs below the window's edge, and a plain
    // focus scrolled the whole page up to reach it: the founder's empty band
    // under the app, which stayed until a reload (2026-11-01).
    target?.focus({ preventScroll: true });
    // Only on opening; focusOn is set in the same event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A press anywhere else closes it, the way every menu on the page does.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onTriggerKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAt('chosen'); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); openAt('last'); }
  };

  const onMenuKey = (e) => {
    const list = items();
    const at = list.indexOf(document.activeElement);
    const go = (i) => { e.preventDefault(); list[(i + list.length) % list.length]?.focus({ preventScroll: true }); };
    switch (e.key) {
      case 'ArrowDown': go(at + 1); break;
      case 'ArrowUp': go(at < 0 ? list.length - 1 : at - 1); break;
      case 'Home': go(0); break;
      case 'End': go(list.length - 1); break;
      case 'Escape':
        // Not the overlay chat's own Escape (which closes the whole chat).
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab': setOpen(false); break;
      case 'Enter':
      case ' ':
        // Handled here for every row alike, the Pro link included (a link
        // does not answer Space on its own).
        if (at >= 0) { e.preventDefault(); list[at].click(); }
        break;
      default:
    }
  };

  const choose = (m) => { onSelect?.(m.id); close(); };
  const toggleDeep = () => { if (!deepOut) onThinkHarder(!thinkHarder); };

  return (
    <div className={`mp${open ? ' is-open' : ''}`} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="ag-inline-select-display mp-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Model: ${onAuto ? 'Auto' : chosen.label}${deepOn ? ', Think harder on' : ''}`}
        onClick={() => (open ? close(false) : openAt('chosen'))}
        onKeyDown={onTriggerKey}
      >
        {onAuto && <span className="mp-spark" aria-hidden="true">✦</span>}
        <span className="mp-trigger-name">{shortName(chosen)}</span>
        {deepOn && <span className="material-symbols-rounded mp-trigger-deep" aria-hidden="true" title="Think harder is on">psychology</span>}
        <span className="material-symbols-rounded mp-caret" aria-hidden="true">expand_more</span>
      </button>

      {open && (
        <>
          <div className="mp-backdrop" aria-hidden="true" onClick={() => close()} />
          <div className="mp-menu" role="menu" id={menuId} aria-label="Model" ref={menuRef} onKeyDown={onMenuKey} style={place || undefined}>
            <button
              type="button"
              role="menuitemradio"
              aria-checked={onAuto}
              data-mp-item=""
              tabIndex={-1}
              className={`mp-item mp-auto${onAuto ? ' is-on' : ''}`}
              onClick={() => choose(auto)}
            >
              <span className="mp-main">
                <span className="mp-name">Auto <span className="mp-pill">Recommended</span></span>
                {autoLines(auto.hint).map((line) => <span key={line} className="mp-line">{line}</span>)}
              </span>
              <Check on={onAuto} />
            </button>

            {models.length > 0 && (
              <div role="group" aria-labelledby={`${menuId}-models`} className="mp-group">
                <div className="mp-group-name" id={`${menuId}-models`}>Choose a model</div>
                {models.map((m) => {
                  const body = (
                    <span className="mp-main">
                      <span className="mp-name">
                        {m.label}
                        {m.low && <span className="mp-low">Low today</span>}
                      </span>
                      {m.bestFor && <span className="mp-line">{m.bestFor}</span>}
                    </span>
                  );
                  if (m.allowed === false && plan && plan !== 'free') {
                    // On a paid plan, a model it cannot use right now (Opus
                    // once Pro's five a day are spent): said, not sold.
                    return (
                      <button
                        key={m.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={false}
                        aria-disabled="true"
                        data-mp-item=""
                        tabIndex={-1}
                        className="mp-item is-locked"
                        onClick={() => {}}
                      >
                        {body}
                        <span className="mp-side">
                          <span className="mp-deep-left">{m.note || 'Not today'}</span>
                          <Check on={false} />
                        </span>
                      </button>
                    );
                  }
                  if (m.allowed === false) {
                    // What Pro adds, shown rather than hidden: the row opens
                    // the plans page and never chooses.
                    return (
                      <Link
                        key={m.id}
                        to="/pricing"
                        role="menuitem"
                        data-mp-item=""
                        tabIndex={-1}
                        className="mp-item is-locked"
                        onClick={() => setOpen(false)}
                      >
                        {body}
                        <span className="mp-side">
                          <CostDots cost={m.cost} />
                          <span className="mp-pill is-pro">Pro<span className="visually-hidden">: see plans</span></span>
                        </span>
                      </Link>
                    );
                  }
                  const on = !onAuto && chosen.id === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={on}
                      data-mp-item=""
                      tabIndex={-1}
                      className={`mp-item${on ? ' is-on' : ''}`}
                      title={m.note || undefined}
                      onClick={() => choose(m)}
                    >
                      {body}
                      <span className="mp-side">
                        <CostDots cost={m.cost} />
                        <Check on={on} />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {showDeep && (
              <div className="mp-deep-wrap">
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={deepOn}
                  aria-disabled={deepOut || undefined}
                  data-mp-item=""
                  tabIndex={-1}
                  className={`mp-item mp-deep${deepOn ? ' is-on' : ''}${deepOut ? ' is-out' : ''}`}
                  onClick={toggleDeep}
                >
                  <span className="mp-main">
                    <span className="mp-name">Think harder</span>
                    <span className="mp-line">Opus 5.5 with extended thinking</span>
                  </span>
                  <span className="mp-side">
                    {left !== null && <span className="mp-deep-left">{deepOut ? 'None left today' : `${left} left today`}</span>}
                    <span className={`mp-switch${deepOn ? ' is-on' : ''}`} aria-hidden="true"><span /></span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
